import Box from '@mui/material/Box';
import Typography from '@material-ui/core/Typography';
import Skeleton from '@mui/material/Skeleton';
import clsx from 'clsx';
import HighlightText from 'components-v2/atoms/HighlightText';
import { getRecentSearchTexts } from 'clients/WebServiceClient';
import LinkComp from 'components/atoms/LinkComp';
import { ICON_HISTORY_CLOCK } from 'constants/Icons';
import { INGREDIENT, PRODUCTS_LOADING_URL, SELLERS, getPathProductBySlug } from 'constants/Paths';
import { NHA_BAN_HANG, THUOC_VA_HOAT_CHAT } from 'constants/data';
import { useHashtagTopSearch } from 'hooks/useHashtagTopSearch';
import { useRouter } from 'next/router';
import { Fragment, memo, useEffect, useState } from 'react';
import ProductServiceV2 from 'services/ProductServiceV2';
import { moveKcbPriorityFirst } from 'services/ProductService';
import useIsKcbVerified from 'hooks/useIsKcbVerified';
import useMaxHeightToViewportBottom from 'hooks/useMaxHeightToViewportBottom';
import { getSellerData } from 'services/SellerService';
import { ImageFallbackStatic } from 'utils/ImageFallback';
import { getLinkProxyFile } from 'utils/ImageUtils';
import { groupArrayOfObjects } from 'utils/ObjectUtils';
import gtag from 'utils/gtag';
import useProductListStates, { userProductListActions } from 'zustand-lib/useProductList';
import styles from './styles.module.css';

function MostSearching({ handleClickOutside }) {
  const { hashtagTopSearch, isLoading } = useHashtagTopSearch();

  useEffect(() => {
    if (isLoading) return;

    setTimeout(() => {
      gtag.displayTopSearch();
      gtag.showTopSearch(hashtagTopSearch);
    }, 300);
  }, [isLoading]);

  return (
    <div className={styles.mostSearching}>
      <Box display="inline" className={styles.label}>
        <Typography display="inline" style={{ fontWeight: '500', fontSize: 14 }}>
          Tìm kiếm nhiều nhất
        </Typography>
      </Box>
      <Box className={styles.results}>
        {isLoading ? (
          <>
            {Array.from({ length: 2 }).map((_, index) => (
              <Skeleton key={index} className={styles.tag} variant="rectangular" animation="wave" width={150} />
            ))}
          </>
        ) : (
          <>
            {hashtagTopSearch.map((_tag) => (
              <LinkComp
                onClick={() => {
                  handleClickOutside(false);
                  gtag.clickTopSearch(_tag);
                }}
                href={_tag.url}
                className={styles.tag}
                key={_tag.hashtag}
                data-test="search-dropdown-enter"
              >
                <div className={styles.dot} />
                <span className={styles.hashtag}>{_tag.hashtag}</span>
                {!!_tag?.svgIcon && (
                  <ImageFallbackStatic width={16} height={16} objectFit="contain" src={getLinkProxyFile(_tag.svgIcon || '')} alt="img" />
                )}
              </LinkComp>
            ))}
          </>
        )}
      </Box>
    </div>
  );
}

const RecentSearchTexts = memo(({ texts = [], searchType = 1 }) => {
  const router = useRouter();
  const { setTriggerClearOverlay } = userProductListActions();

  function handleClickRecentKeyword(val, searchType) {
    if(router.pathname.includes('[slug]')) {
      delete router.query.slug;
    }
    if (val && searchType) {
      router.push({
        pathname: `/products`,
        query: { ...router.query, q: val, searchType: searchType },
      });
    } else if (val) {
      router.push({
        pathname: `/products`,
        query: { ...router.query, q: val },
      });
    }
    setTriggerClearOverlay();
  }

  return (
    <Box>
      <div className={styles.recentSearchTextContainer}>
        <ICON_HISTORY_CLOCK /> <span className={styles.recentSearchTextContent}>Lịch sử tìm kiếm</span>
      </div>
      {texts.map((text) => (
        <div className={styles.hoveredRecentSearchText} onClick={() => handleClickRecentKeyword(text, searchType)} key={text} role="button">
          {text}
        </div>
      ))}
    </Box>
  );
});

RecentSearchTexts.displayName = 'RecentSearchTexts';

// Tiêu đề từng nhóm kết quả: "<keyword> trong <nhóm>"
function SectionHeader({ keyword, label, href, ...restProps }) {
  return (
    <LinkComp className={clsx(styles.searchResults, styles.sectionHeader)} href={href} {...restProps}>
      <Box display="inline" className={styles.keyword}>
        <Typography display="inline">{keyword}</Typography>
      </Box>
      &nbsp;trong {label}
    </LinkComp>
  );
}

function SearchDropdown(props) {
  const { data, keyword, type, handleClickOutside, ingredients = [] } = props;
  const isKcbVerified = useIsKcbVerified();
  // BV-2197: khung kết quả kéo dài xuống gần đáy màn hình để hiện được nhiều kết quả hơn
  const [dropdownRef, dropdownMaxHeight] = useMaxHeightToViewportBottom(50);
  const [recentSearchTexts, setRecentSearchTexts] = useState([]);
  const { triggerClearOverlay } = useProductListStates();
  const { clearTriggerClearOverlay } = userProductListActions();

  useEffect(() => {
    let isMounted = true;
    const fetchRecentSearchTexts = async () => {
      const resp = await getRecentSearchTexts();
      if (resp.status === 'OK' && isMounted) {
        setRecentSearchTexts(resp.data || []);
      }
    };
    if (keyword === '') {
      fetchRecentSearchTexts();
    }
    return () => {
      isMounted = false;
    };
  }, [keyword]);

  useEffect(() => {
    if (triggerClearOverlay > 0) {
      handleClickOutside();
    }

    return () => {
      clearTriggerClearOverlay();
    };
  }, [triggerClearOverlay]);

  if (!data || !type) {
    return (
      <Box className={styles.searchDropdown} data-test="search-dropdown">
        <Typography display="inline" className={styles.searchResults}>
          Không có sản phẩm với từ khóa <Typography display="inline">{keyword}</Typography>&nbsp;&nbsp;trong&nbsp;
          <Typography className={styles.textPrimary}>Tất Cả Sản Phẩm</Typography>
        </Typography>
      </Box>
    );
  }

  // kq của thuốc
  if (data && type === THUOC_VA_HOAT_CHAT) {
    const listSearch = data.map((item) => {
      const info = ProductServiceV2.convertProductV2(item);
      const { displayPriceFormated, displayPrice, tags, name, sellerInfo: seller } = info;
      const sellerInfo = getSellerData({ seller, tags });

      return {
        ...item,
        name,
        displayPriceFormated,
        displayPrice,
        sellerName: sellerInfo?.individualNonVatSellerName || sellerInfo?.sellerName || '',
      };
    });

    const groupedProductByName = groupArrayOfObjects(listSearch, 'name');

    return (
      <Box ref={dropdownRef} className={styles.searchDropdown} style={{ maxHeight: dropdownMaxHeight }} data-test="search-dropdown">
        {keyword !== '' && (
          <SectionHeader keyword={keyword} label="Thuốc" href={`${PRODUCTS_LOADING_URL}?text=${keyword}`} data-test="search-dropdown-enter" />
        )}
        <Box className={styles.styledScroll}>
          {Object.keys(groupedProductByName).map((key) => {
            const listProduct = groupedProductByName[key];

            return moveKcbPriorityFirst(
              listProduct?.sort((a, b) => a?.displayPrice - b?.displayPrice),
              isKcbVerified,
            )?.map((item) => {
              // BV-2197: hoạt chất khớp keyword do BE trả -> "Có chứa <hoạt chất>"
              const matchedIngredients = item?.product?.matchedIngredients || [];

              // hard code flagship store
              return (
                <LinkComp
                  className={styles.searchResults}
                  key={`${key}-${item.sku.slug}`}
                  href={getPathProductBySlug(item.sku.slug)}
                  data-test="search-dropdown-item"
                >
                  <HighlightText text={item?.product?.name} ranges={item?.product?.nameHighlights} />{' '}
                  {listProduct.length > 1 && `${item?.sellerName && `- ${item?.sellerName}`}  - ${item?.displayPriceFormated}`}
                  {matchedIngredients.length > 0 && (
                    <span className={styles.ingredientHint}>
                      Có chứa{' '}
                      <i>
                        {matchedIngredients.map((ingredient, index) => (
                          <Fragment key={ingredient.slug || ingredient.name}>
                            {index > 0 && ', '}
                            <HighlightText text={ingredient.name} ranges={ingredient.highlights} />
                          </Fragment>
                        ))}
                      </i>
                    </span>
                  )}
                </LinkComp>
              );
            });
          })}
        </Box>
        {keyword !== '' && ingredients?.length > 0 && (
          <Box className={styles.ingredientSection} data-test="search-dropdown-ingredients">
            <SectionHeader keyword={keyword} label="Hoạt chất" href={INGREDIENT} />
            {ingredients.map((ingredient) => (
              <LinkComp className={styles.searchResults} key={ingredient.code || ingredient.slug} href={`${INGREDIENT}/${ingredient.slug}`}>
                <HighlightText text={ingredient.name} ranges={ingredient.highlights} />
              </LinkComp>
            ))}
          </Box>
        )}
        {data?.length <= 0 && keyword === '' && <RecentSearchTexts texts={recentSearchTexts} />}
        <MostSearching handleClickOutside={handleClickOutside} />
      </Box>
    );
  }

  // kq của nhà bán hàng
  if (data && type === NHA_BAN_HANG) {
    return (
      <Box ref={dropdownRef} className={styles.searchDropdown} style={{ maxHeight: dropdownMaxHeight }} data-test="search-dropdown">
        {keyword !== '' && (
          <SectionHeader keyword={keyword} label="Nhà bán hàng" href={`${SELLERS}?search=${keyword}`} />
        )}
        <Box className={styles.styledScroll}>
          {data?.map((item) => (
            <LinkComp className={styles.searchResults} key={item.name} href={item.link}>
              {item.name}
            </LinkComp>
          ))}
        </Box>
        {data?.length <= 0 && keyword === '' && <RecentSearchTexts texts={recentSearchTexts} />}
        <MostSearching handleClickOutside={handleClickOutside} />
      </Box>
    );
  }

  return (
    <Box className={styles.searchDropdown} data-test="search-dropdown">
      <Typography display="inline" className={styles.searchResults}>
        Không có sản phẩm với từ khóa <Typography display="inline">{keyword}</Typography>&nbsp;&nbsp;trong&nbsp;
        <Typography className={styles.textPrimary}>Tất Cả Sản Phẩm</Typography>
      </Typography>
    </Box>
  );
}

export default memo(SearchDropdown);
MostSearching.displayName = 'MostSearching';
